
import { Component, Prop, Vue, Watch } from 'bits/vue'
import Hammer from 'hammerjs'
import OzOverlay from 'library/v4/components/OzOverlay.vue'

/**
 * Mobile Drag-to-expand Drawer Modal
 */

@Component({ components: { OzOverlay } })
export default class OzDrawerDraggable extends Vue {
  $refs!: {
    header: HTMLElement
    modalBody: HTMLDivElement
    modalBodyContent: HTMLDivElement
    modalContainer: HTMLDivElement
    modalContainerInner: HTMLDivElement
    modalContainerInnerWrapper: HTMLDivElement
  }

  /**
   * Show or hide header box
   */
  @Prop({ default: true }) xHeader!: boolean
  /**
   * Current windowHeight, used to compute drawer measurement
   */
  @Prop({ required: true }) windowHeight!: number
  /**
   * To show drawer handle
   */
  @Prop({ default: false }) xDrawerHandle!: boolean
  /**
   * To force full size drawer
   */
  @Prop({ default: false }) fullSize!: boolean
  /**
   * To force disable drag
   */
  @Prop({ default: false }) disableDrag!: boolean
  /**
   * When content is overflow, choose to scroll the whole modal or just the body
   */
  @Prop({ default: 'body' }) scrollType!: 'body' | 'whole'
  /**
   * Set dark mode
   */
  @Prop({ default: 'auto' }) darkMode!: true | false | 'auto'
  /**
   * Mobile preferred start height. If device is too small we self compute this
   */
  @Prop({ default: undefined }) preferredTrayStartHeight?: number
  /**
   * If the drawer should cover the whole screen when full size
   */
  @Prop({ default: false }) shouldFullSizeCoverScreen!: boolean
  /**
   * Show a scrim.
   */
  @Prop({ default: true }) xScrim!: boolean

  /**
   * Decides if scrim is not really there and can be clicked through
   **/
  @Prop({ default: false }) isScrimSpectral!: boolean

  /**
   * Determines whether the modal should fade in when opened
   */
  @Prop({ default: true }) shouldFadeIn!: boolean

  /**
   * Height of mobile "saved space" at the top that the drawer can never touch
   */
  @Prop({ default: 0 }) minTopMargin!: number

  /**
   *  TailwindCSS z-index class.
   *
   *  - `z-modal2` is for all dialogs
   *  - `z-sidepanel` is for surface sidepanels
   */
  @Prop({ required: false }) zIndexClass?: 'z-modal2' | 'z-sidepanel'

  /**
   * Determines whether the scrollbar should be hidden
   */
  @Prop({ required: false }) hideScrollbar?: boolean

  // phone
  MAX_TOP_MARGIN = 0
  MIN_TOP_MARGIN = 0
  DRAG_DISTANCE_AT_TOP = 0
  DRAG_DISTANCE_MIN = 0

  dragDistanceY = 0
  dragDistanceYBeforeStart = 0

  latestVelocityY = 0 // for computing end velocity
  latestPanEventTimestamp = 0 // for computing end velocity
  draggedToFullSize = false
  hammer?: HammerManager
  scrollEnabled = false
  panEnabled = false
  marginTop = 0

  @Watch('disableDrag')
  handleDragDisable(disabled: boolean): void {
    if (disabled) {
      this.removePanListener()
    } else {
      this.addPanListener()
    }
  }

  @Watch('fullSize')
  handleResize(fullSize: boolean): void {
    // For mobile, we simulate a drag with an animation.
    if (fullSize) {
      this.resetElement('full', 'slow')

      setTimeout(() => {
        // reset scrollTop for iOS
        // when keyboard pops up, <html> will scroll down a bit
        document.documentElement.scrollTop = 0
      }, 100)
      this.removePanListener()
    } else {
      this.resetElement('top', 'slow')
      this.addPanListener()
    }
  }

  updateElement(): void {
    this.$nextTick(() => {
      const modalContainer = this.$refs.modalContainer
      if (!modalContainer) return
      this.marginTop = this.MAX_TOP_MARGIN + this.dragDistanceY
      if (this.marginTop === this.MIN_TOP_MARGIN) {
        this.draggedToFullSize = true
      } else {
        this.draggedToFullSize = false
      }

      modalContainer.style.marginTop = this.marginTop + 'px'
    })
  }

  addTransition(transitionSpeed: 'slow' | 'fast' = 'slow'): void {
    const modalContainer = this.$refs.modalContainer
    if (!modalContainer) return
    modalContainer.style.transition =
      transitionSpeed === 'slow'
        ? `margin 150ms cubic-bezier(0.4, 0, 0.2, 1)`
        : `margin 50ms cubic-bezier(0.4, 0, 0.2, 1)`
    setTimeout(
      () => {
        modalContainer.style.transition = ''
      },
      transitionSpeed === 'slow' ? 150 : 100,
    )
  }

  resetElement(
    position: 'full' | 'top' | 'bottom' | 'out' = 'bottom',
    transitionSpeed: 'slow' | 'fast' = 'slow',
  ): void {
    this.addTransition(transitionSpeed)
    if (position === 'bottom') {
      this.dragDistanceY = 0
      this.disableScroll()
    } else if (position === 'top') {
      this.dragDistanceY = this.DRAG_DISTANCE_AT_TOP
      this.enableScroll()
    } else if (position === 'full') {
      this.dragDistanceY = -this.MAX_TOP_MARGIN
      this.enableScroll()
    } else if (position === 'out') {
      this.dragDistanceY = this.windowHeight
    }
    this.updateElement()
  }

  /**
   * Compute end velocity based on panEnd event and the latest panMove event to decide if the user is
   * swiping up/down to expand/contract the tray.
   * Sometimes end event can have the incorrect velocity that make it think that user didn't swipe fast enough.
   */
  computeEndVelocity({ endEvent, lastPanEvent }: Record<string, { velocity: number; timestamp: number }>): number {
    const direction = (endEvent.velocity + lastPanEvent.velocity) / Math.abs(endEvent.velocity + lastPanEvent.velocity)
    if (endEvent.timestamp - lastPanEvent.timestamp > 60) {
      return direction * Math.max(Math.abs(lastPanEvent.velocity), endEvent.velocity) * 1000
    }
    return direction * Math.abs(endEvent.velocity) * 1000
  }

  /* SCROLLING */

  scrollTimeout?: number
  scrollTop = 0
  touchStartY?: number

  get isScrollingWholeModal(): boolean {
    return this.scrollType === 'whole'
  }

  /**
   * When scrolling whole modal (header scroll together with body), innerWrapper is scrolled
   * When only the body is scrolling, modalBody is scrolled
   */
  get scrollingElement(): HTMLDivElement {
    return this.isScrollingWholeModal ? this.$refs.modalContainerInnerWrapper : this.$refs.modalBody
  }

  isDragStartEmittedForScroll = false

  /**
   * Handler for touchstart event for modal body
   */
  handleTrayScrollStart(e: TouchEvent): void {
    this.touchStartY = e.touches[0].clientY
    this.isDragStartEmittedForScroll = false
  }

  /**
   * Handler for touchmove event for modal body.
   * Here we have the logic to decide if hammerjs should take over the event to pan the modal
   */
  handleTrayScroll(e: TouchEvent): void {
    if (!this.scrollEnabled) {
      if (e.cancelable) {
        e.preventDefault()
      }
      return
    }
    if (!this.isDragStartEmittedForScroll) {
      this.isDragStartEmittedForScroll = true
      this.$emit('dragstart')
    }
    const element = this.scrollingElement
    if (!element) return
    if (this.panEnabled && element.scrollTop < 0) {
      e.preventDefault() // scroll more than the top = scroll up -- prevent default so that Hammer can pan.
    } else if (this.panEnabled && element.scrollTop === 0) {
      if (this.touchStartY && this.touchStartY > e.changedTouches[0].clientY + 5) {
        // scroll down -- let it scroll
      } else if (this.touchStartY && this.touchStartY < e.changedTouches[0].clientY - 5) {
        // scroll up -- prevent default so that Hammer can pan.
        if (e.cancelable) {
          e.preventDefault()
        }
      }
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
      this.scrollTimeout = undefined
    }
    this.scrollTimeout = setTimeout(() => {
      this.scrollTop = element.scrollTop
      const EPSILON = 5 // on iOS if we scroll to the top, the scroll is bounced back by a little
      if (this.scrollTop <= EPSILON) {
        this.addPanListener()
      } else {
        this.removePanListener()
      }
    }, 250) as unknown as number
  }

  /**
   * Enable scrolling when dragging the modal (instead of moving it -- also need to prevent moving)
   */
  enableScroll(): void {
    this.scrollEnabled = true
    this.scrollingElement.style.overflow = 'auto'
  }

  /**
   * Disable scrolling when dragging the modal (will move it instead -- need to let moving happen)
   */
  disableScroll(): void {
    this.scrollEnabled = false
    this.scrollingElement.style.overflow = 'visible'
  }

  /**
   * DRAGGING (hammerjs events)
   */

  scrollWithPan = false // see explanation 10 lines below

  handleStartPan(): void {
    this.dragDistanceYBeforeStart = this.dragDistanceY
    const element = this.scrollingElement
    if (this.scrollEnabled && element.scrollTop === 0) {
      this.scrollWithPan = true
    }
    /**
     * Drag start event, can be used to disable interaction to items in content to prevent mishandled interactions
     */
    this.$emit('dragstart')
  }

  handlePanMove(distance: number, velocity: number, timestamp: number): void {
    if (this.scrollWithPan && distance < 0) {
      // Rarely happen:
      // scrolling is enabled but default-prevented and user is scrolling down
      // we will scroll manually for 1 step; afterwards native scroll events will
      // be detected and will happen normally
      const element = this.scrollingElement
      element.scrollTop = -distance
      this.scrollWithPan = false
    } else {
      // Most of the time:
      // Move the tray according to mouse drag distance
      this.dragDistanceY = Math.max(this.dragDistanceYBeforeStart + distance, this.DRAG_DISTANCE_AT_TOP)
      this.updateElement() // really update the element to move
      this.latestVelocityY = velocity
      this.latestPanEventTimestamp = timestamp
      /**
       * Dragging event, emit the distance dragged
       */
      this.$emit('dragging', distance)
    }
  }

  handlePanEnd(velocity: number, timestamp: number): void {
    const endVelocity = this.computeEndVelocity({
      endEvent: { velocity, timestamp },
      lastPanEvent: { velocity: this.latestVelocityY, timestamp: this.latestPanEventTimestamp },
    })
    if (this.dragDistanceY <= this.DRAG_DISTANCE_AT_TOP) {
      this.dragDistanceY = this.DRAG_DISTANCE_AT_TOP
      this.enableScroll()
    }

    const isAtTopBeforeDragging = this.dragDistanceYBeforeStart <= this.DRAG_DISTANCE_AT_TOP

    if (endVelocity > 300) {
      /**
       * Swipedown event, with { isAtTopBeforeDragging: boolean } as args. Can be used to trigger closing the drawer for isAtTopBeforeDragging === false
       * @param args
       * @param args.isAtTopBeforeDragging  boolean
       */
      this.$emit('swipedown', { isAtTopBeforeDragging })
      if (this.latestVelocityY + velocity > 0) this.resetElement('bottom', 'fast')
      else this.resetElement('top', 'fast')
    } else if (endVelocity < -300) {
      // Is swiping, so we scroll to the top/bottom
      /**
       * Swipeup event, with { isAtTopBeforeDragging: boolean } as args
       * @param args
       * @param args.isAtTopBeforeDragging  boolean
       */
      this.$emit('swipeup', { isAtTopBeforeDragging })
      if (this.latestVelocityY + velocity < 0) this.resetElement('top', 'fast')
      else this.resetElement('bottom', 'fast')
    } else {
      // Not swiping, so we detect the end location and decide to scroll to top/bottom
      if (this.dragDistanceY > this.DRAG_DISTANCE_AT_TOP / 2) {
        this.resetElement('bottom', 'slow')
      } else if (this.DRAG_DISTANCE_AT_TOP / 2 > this.dragDistanceY && this.dragDistanceY > this.DRAG_DISTANCE_AT_TOP) {
        this.resetElement('top', 'slow')
      }
    }

    /**
     * Drag end event, can be used to re-enable interaction to items in content after disabling in dragstart
     */
    this.$emit('dragend', this.dragDistanceY)
  }

  addPanListener(): void {
    if (this.fullSize) return
    if (this.disableDrag) {
      this.enableScroll()
      return
    }
    if (!this.hammer) {
      const modalContainer = this.$refs.modalContainerInner
      this.hammer = new Hammer(modalContainer)
    }
    this.hammer.get('pan').set({ direction: Hammer.DIRECTION_VERTICAL })
    this.hammer.on('panstart', (e) => {
      this.handleStartPan()
    })

    this.hammer.on('panmove', (e) => {
      this.handlePanMove(e.deltaY, e.velocityY, e.timeStamp)
    })

    this.hammer.on('panend', (e) => {
      this.handlePanEnd(e.velocityY, e.timeStamp)
    })
    this.panEnabled = true
  }

  removePanListener(): void {
    if (!this.hammer) return
    this.hammer.off('panstart panmove panend')
    this.panEnabled = false
  }

  /**
   * COMPONENTS LIFECYCLE
   * */

  /**
   * When the component mount, with the device information we calculate the measurements
   * necessary for the bottom tray to be work beautifully
   */
  computePrecalculatedMeasurement(): void {
    this.MAX_TOP_MARGIN = this.preferredTrayStartHeight
      ? this.windowHeight - this.preferredTrayStartHeight
      : this.windowHeight / 4 // Leave a maximum of windowHeight/4 px gap at the top for when the drawer is not expanded
    this.MIN_TOP_MARGIN = ((): number => {
      if (this.shouldFullSizeCoverScreen) return this.minTopMargin
      const scrollingContent = this.$refs.modalBodyContent
      if (scrollingContent) {
        const min = this.windowHeight - scrollingContent.clientHeight - this.$refs.header?.clientHeight
        if (min > this.MAX_TOP_MARGIN) return this.minTopMargin
        if (min < 0) return this.minTopMargin
        return min
      }
      return this.minTopMargin
    })()
    this.DRAG_DISTANCE_AT_TOP = -this.MAX_TOP_MARGIN + Math.max(0, this.MIN_TOP_MARGIN)
    this.DRAG_DISTANCE_MIN = -this.MAX_TOP_MARGIN + this.MIN_TOP_MARGIN
  }

  /**
   * For small devices, we start with the bottom tray slided down using a top margin
   */
  setStartingTopMargin(): void {
    this.marginTop = this.MAX_TOP_MARGIN
    if (this.fullSize || this.draggedToFullSize) this.dragDistanceY = this.DRAG_DISTANCE_AT_TOP
    this.updateElement()
  }

  @Watch('preferredTrayStartHeight')
  recalculateMeasurement(): void {
    this.$nextTick(() => {
      this.computePrecalculatedMeasurement()
      this.setStartingTopMargin()
    })
  }

  mounted(): void {
    this.computePrecalculatedMeasurement()
    this.setStartingTopMargin()
    this.addPanListener()
  }

  scrimClick(): void {
    /**
     * Emitted with overlay scrim is clicked. Can be used to trigger closure of drawer.
     */
    this.$emit('scrim-click')
  }

  scrimEsc(): void {
    /**
     * Emitted when Escape key is pressed. Can be used to trigger closure of drawer.
     */
    this.$emit('scrim-esc')
  }
}
